/**
 * Contract for the app's notification toasts — the single surface for transient
 * success / error / warning / info messages. See "Notification Toasts" in
 * docs/design-system.md for the visual and behavioural standard.
 */

/** The notification types. `pending` is the in-flight state of a long-running
 *  action — it is shown the moment the action starts and replaced in place with
 *  its outcome via `updateToast`. */
export type ToastVariant = 'success' | 'error' | 'warning' | 'info' | 'pending';

/** What a caller passes to `showToast`. */
export type ToastRequest = {
  state?: ToastVariant;
  message: string;
  /** Auto-dismiss in ms. Omit to take the per-variant default; negative keeps
   *  the toast up until the user dismisses it. */
  delay?: number;
  persistent?: boolean;
  /** 0–100. Renders a slim progress bar under the message — for a `pending`
   *  toast that tracks a long-running background job (an upload, server-side
   *  processing). Update it in place with `updateToast`; omit for every other
   *  toast. */
  progress?: number;
  action?: ToastAction;
  /** PLAT-11877 — a side action beside the main one ("Copy Review Link" next
   *  to "Review Translation"). Unlike `action`, pressing it does NOT dismiss
   *  the toast: it is a detour, and the main action must stay reachable. */
  secondaryAction?: ToastAction;
};

export type ToastAction = {
  label: string;
  onClick: () => void;
};

/** A queued toast: a request plus its id and resolved dismissal delay. */
export type Toast = ToastRequest & { id: number; delay: number };

/** How long each variant stays up when the caller doesn't say. Errors are
 *  sticky — an unread failure is worse than a lingering toast. `pending` is
 *  sticky too: it ends when the action resolves, not on a timer. */
const DEFAULT_DELAY_MS: Record<ToastVariant, number> = {
  success: 4000,
  info: 4000,
  warning: 6000,
  error: -1,
  pending: -1,
};

/** A toast carrying an action needs long enough to be acted on. */
const ACTIONABLE_DELAY_MS = 8000;

/** At most this many toasts are visible; older ones drop off the bottom. */
export const MAX_VISIBLE_TOASTS = 3;

export const resolveToastDelay = (toast: ToastRequest): number => {
  if (toast.delay !== undefined) return toast.delay;
  const variant = toast.state ?? 'success';
  const base = DEFAULT_DELAY_MS[variant];
  if (base < 0) return base;
  return toast.action ? ACTIONABLE_DELAY_MS : base;
};
